import styled from '@emotion/styled'
import { Button } from 'app/components/Common/Button'
import { Logo } from 'app/components/Icons'
import { FlexBox } from 'app/components/Layout/FlexBox'
import { SEO } from 'app/components/SEO'
import { useVocabularyData } from 'app/utils/vocabulary'
import React, { memo, useEffect, useState } from 'react'

import text from './text.json'

type Contents = (typeof text)['it']
const validLanguageCodes = Object.keys(text)

export const Head = () => {
  return <SEO />
}

export default memo(function Error404() {
  const [contents, setContents] = useState<Contents>(text.en)

  useEffect(() => {
    const language =
      (window.navigator as any).userLanguage || window.navigator.language
    const languageCode = language.substring(0, 2).toLowerCase()

    if (validLanguageCodes.includes(languageCode)) {
      setContents(text[languageCode as 'it'])
    }
  }, [])

  return (
    <Container>
      <Wrapper>
        <Logo />

        <Title>{contents.title}</Title>

        <Text>{contents.text}</Text>

        <CTA
          label={useVocabularyData('back-to-home', contents.code)}
          URL={`/${contents.prefix}`}
          variant="light"
        />
      </Wrapper>
    </Container>
  )
})

const Container = styled(FlexBox)`
  width: 100%;
  height: 100%;
  background: ${({ theme }) => theme.colors.variants.primaryDark};
  position: fixed;
  top: 0;
  left: 0;
`

const Wrapper = styled.div`
  position: absolute;
  top: 50%;
  right: 1.25rem;
  left: 1.25rem;
  text-align: center;
  transform: translateY(-50%);

  > svg {
    width: 16.125rem;
    height: 2.5625rem;
    fill: ${({ theme }) => theme.colors.variants.neutralLight4};
    margin-bottom: 2.5rem;
  }
`

const Title = styled.h1`
  color: ${({ theme }) => theme.colors.variants.neutralLight4};
  font-family: ${({ theme }) => theme.fontFamily.heading};
  font-size: 3.75rem;
  font-weight: 300;
  line-height: 3.875rem;

  @media (max-width: 1199px) {
    font-size: 2.5rem;
    line-height: 3rem;
  }
`

const Text = styled.div`
  color: ${({ theme }) => theme.colors.variants.neutralLight2};
  font-family: ${({ theme }) => theme.fontFamily.paragraph};
  font-size: 1rem;
  font-weight: 300;
  line-height: 1.375rem;
  margin-top: 1.25rem;
`

const CTA = styled(Button)`
  margin-top: 3.75rem;

  @media (max-width: 1199px) {
    margin-top: 3.125rem;
  }
`
